import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Wallet, TrendingUp, BarChart3, Gift, Store, User, Bell, Smartphone, Monitor, LogOut } from 'lucide-react';
import { GDLLogo } from './GDLLogo';
import GDLAskAtomFab from './GDLAskAtomFab';
import './gdl.css';

const NAV_ITEMS = [
  { key: 'home', label: 'Home', icon: Home, path: '/demo/gdl/dashboard' },
  { key: 'wallet', label: 'Wallet', icon: Wallet, path: '/demo/gdl/wallet' },
  { key: 'invest', label: 'Invest', icon: TrendingUp, path: '/demo/gdl/invest' },
  { key: 'stocks', label: 'Stocks', icon: BarChart3, path: '/demo/gdl/stocks' },
  { key: 'benefits', label: 'Benefits', icon: Gift, path: '/demo/gdl/benefits' },
  { key: 'vendors', label: 'Vendors', icon: Store, path: '/demo/gdl/vendors' },
  { key: 'profile', label: 'Profile', icon: User, path: '/demo/gdl/profile' },
];

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

/* ── Mobile Header ──────────────────────── */
function MobileHeader({ user }) {
  return (
    <div style={{
      height: 88, background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(16px)',
      borderBottom: '1px solid rgba(176,49,64,0.08)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
      padding: '0 20px 12px 20px', flexShrink: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%', background: '#B03140',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 600, color: '#fff',
          boxShadow: '0 2px 8px rgba(176,49,64,0.25)',
        }}>
          {getInitials(user?.full_name)}
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#8E2233', fontWeight: 500, opacity: 0.6 }}>Welcome back</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#2D0A10' }}>{user?.full_name?.split(' ')[0] || 'User'}</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%', background: 'rgba(176,49,64,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Bell size={18} color="#8E2233" />
        </div>
      </div>
    </div>
  );
}

/* ── Bottom Nav ─────────────────────────── */
function BottomNav({ activeKey, onNavigate }) {
  return (
    <div style={{
      height: 68, background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(16px)',
      borderTop: '1px solid rgba(176,49,64,0.06)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-around',
      flexShrink: 0, paddingBottom: 6, paddingTop: 2,
    }} data-testid="bottom-nav">
      {NAV_ITEMS.map(item => {
        const active = activeKey === item.key;
        const Icon = item.icon;
        return (
          <button key={item.key} onClick={() => onNavigate(item.path)}
            data-testid={`nav-${item.key}`}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px 2px',
            }}
          >
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: active ? '#B03140' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s ease',
              boxShadow: active ? '0 2px 8px rgba(176,49,64,0.3)' : 'none',
            }}>
              <Icon size={18} color={active ? '#fff' : '#A07078'}
                style={active ? { animation: 'gdlIconBounce 0.3s ease' } : {}} />
            </div>
            <span style={{ fontSize: 9, fontWeight: 600, color: active ? '#B03140' : '#A07078', letterSpacing: '0.02em' }}>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ── Desktop Sidebar ────────────────────── */
function Sidebar({ activeKey, onNavigate, user, onLogout }) {
  return (
    <div style={{
      width: 240, background: '#B03140',
      display: 'flex', flexDirection: 'column', flexShrink: 0, height: '100vh',
    }} data-testid="sidebar">
      <div style={{ height: 72, display: 'flex', alignItems: 'center', padding: '0 24px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <GDLLogo height={28} showText={false} />
        <span style={{ marginLeft: 10, fontSize: 20, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>GDL+</span>
      </div>
      <div style={{ padding: '20px 12px', flex: 1 }}>
        <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.4)', padding: '0 14px', marginBottom: 10 }}>
          Menu
        </div>
        {NAV_ITEMS.map(item => {
          const active = activeKey === item.key;
          const Icon = item.icon;
          return (
            <button key={item.key} onClick={() => onNavigate(item.path)}
              data-testid={`sidebar-${item.key}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                height: 42, padding: '0 14px', borderRadius: 10, border: 'none',
                background: active ? 'rgba(255,255,255,0.15)' : 'transparent',
                color: active ? '#fff' : 'rgba(255,255,255,0.65)', cursor: 'pointer',
                fontSize: 14, fontWeight: active ? 600 : 400, marginBottom: 2,
                transition: 'all 0.15s ease',
              }}
            >
              <Icon size={18} color={active ? '#fff' : 'rgba(255,255,255,0.5)'} />
              {item.label}
            </button>
          );
        })}
      </div>
      <div style={{ padding: '16px 12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px 12px' }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 600, color: '#fff',
          }}>
            {getInitials(user?.full_name)}
          </div>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.8)' }}>{user?.full_name}</span>
        </div>
        <button onClick={onLogout} data-testid="sidebar-logout"
          style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px',
            background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 10, color: 'rgba(255,255,255,0.6)',
            fontSize: 13, cursor: 'pointer', transition: 'all 0.15s ease',
          }}
        >
          <LogOut size={16} /> Sign Out
        </button>
      </div>
    </div>
  );
}

/* ── Desktop Header ─────────────────────── */
function DesktopHeader({ user }) {
  const location = useLocation();
  const current = NAV_ITEMS.find(n => n.path === location.pathname);
  return (
    <div style={{
      height: 64, background: '#fff', borderBottom: '1px solid rgba(176,49,64,0.08)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px',
      flexShrink: 0,
    }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#2D0A10', letterSpacing: '-0.02em' }}>{current?.label || 'Dashboard'}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%', background: 'rgba(176,49,64,0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Bell size={18} color="#8E2233" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: '50%', background: '#B03140',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 600, color: '#fff',
          }}>
            {getInitials(user?.full_name)}
          </div>
          <span style={{ fontSize: 14, fontWeight: 500, color: '#2D0A10' }}>{user?.full_name}</span>
        </div>
      </div>
    </div>
  );
}

/* ── View Toggle Button ─────────────────── */
function ViewToggle({ mode, onToggle }) {
  const [hovered, setHovered] = useState(false);
  const Icon = mode === 'mobile' ? Monitor : Smartphone;
  const label = mode === 'mobile' ? 'Desktop' : 'Mobile';
  return (
    <button
      onClick={onToggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid="view-toggle"
      style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
        height: 44, borderRadius: 22, border: 'none',
        background: '#B03140', color: '#fff', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 6,
        padding: hovered ? '0 16px 0 12px' : '0 11px',
        boxShadow: '0 4px 20px rgba(176,49,64,0.35)',
        transition: 'all 0.25s ease', overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      <Icon size={20} />
      {hovered && <span style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>}
    </button>
  );
}

/* ── Main Layout ────────────────────────── */
export default function GDLLayout({ children, showNav = true }) {
  const nav = useNavigate();
  const location = useLocation();
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('gdl_view_mode') || 'mobile');

  const user = JSON.parse(localStorage.getItem('gdl_demo_user') || '{}');
  const activeKey = NAV_ITEMS.find(n => n.path === location.pathname)?.key || 'home';

  useEffect(() => {
    localStorage.setItem('gdl_view_mode', viewMode);
  }, [viewMode]);

  const handleLogout = () => {
    localStorage.removeItem('gdl_demo_user');
    localStorage.removeItem('gdl_demo_token');
    nav('/demo/gdl/login');
  };

  const toggleMode = () => setViewMode(m => m === 'mobile' ? 'desktop' : 'mobile');
  const handleNavigate = (path) => nav(path);

  if (viewMode === 'mobile') {
    return (
      <>
        <div className="gdl-phone-shell">
          <div className="gdl-phone-frame">
            <div className="gdl-phone-notch" />
            {showNav && <MobileHeader user={user} onLogout={handleLogout} />}
            <div className="gdl-scroll" style={{ flex: 1, overflowY: 'auto', background: '#F5E1E4' }}>
              {children}
            </div>
            {showNav && <BottomNav activeKey={activeKey} onNavigate={handleNavigate} />}
            <div className="gdl-phone-home-indicator" />
            {showNav && <GDLAskAtomFab csId={user?.id} />}
          </div>
        </div>
        <ViewToggle mode={viewMode} onToggle={toggleMode} />
      </>
    );
  }

  // Desktop mode
  return (
    <>
      <div style={{ display: 'flex', height: '100vh', background: '#F5E1E4' }}>
        {showNav && <Sidebar activeKey={activeKey} onNavigate={handleNavigate} user={user} onLogout={handleLogout} />}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {showNav && <DesktopHeader user={user} />}
          <div className="gdl-scroll" style={{ flex: 1, overflowY: 'auto', padding: showNav ? '24px 32px' : 0, background: '#F5E1E4' }}>
            <div style={{ maxWidth: showNav ? 1200 : '100%', margin: '0 auto' }}>
              {children}
            </div>
          </div>
        </div>
      </div>
      <ViewToggle mode={viewMode} onToggle={toggleMode} />
    </>
  );
}
